import React, {useRef, useState} from "react";
import {Alert, Box, Button, Grid, TextField, Typography,} from "@mui/material";
import styled from "@emotion/styled";
import {useNavigate} from "react-router-dom";
import {forgotPasswordApi} from "../../api/authApi";
import LoginLeftPart from "../../components/LoginLeftPart";
import LightOnlyPage from "../../components/LightOnlyPage";
import "./style.css";
import LabelText from "../../components/Common/LabelText";
import TextFieldBox from "../../components/Common/TextFieldBox";
import {errorDetail} from "../../helper/errorDetail";
import {validateEmail} from "../../helper/validation";
import { authFormFieldsSx } from "../Onboarding/fieldStyles";
import { primaryButtonSx } from "../Onboarding/registrationShared";
import useResendCooldown from "../../hooks/useResendCooldown";

const COOLDOWN_STORAGE_KEY = "forgot-password-cooldown";
const SUCCESS_COOLDOWN_SECONDS = 60;
const WARNING_STORAGE_KEY = "forgot-password-warning";
const SUCCESS_MESSAGE = "If an account exists for that address we've sent a reset link. Check your inbox.";
const RATE_LIMIT_MESSAGE = "Too many password reset requests. Please try again later.";
const SERVER_ERROR_MESSAGE = "The server could not process your request. Please try again later.";
const REQUEST_ERROR_MESSAGE = "We could not process your request. Check your email address and try again.";
const CONNECTION_ERROR_MESSAGE = "We could not reach the server. Check your connection and try again.";

// A rate-limit outcome has no known expiry and must never use the cooldown record.
function loadWarning() {
  try {
    return window.sessionStorage.getItem(WARNING_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function persistWarning(message) {
  try {
    if (message) window.sessionStorage.setItem(WARNING_STORAGE_KEY, message);
    else window.sessionStorage.removeItem(WARNING_STORAGE_KEY);
  } catch {
    // Persistence is optional; the current outcome still renders when storage is blocked.
  }
}

export const StyledTypography = styled(Typography)(() => ({
  color: "#000000",
  fontWeight: "700",
  fontStyle: "normal",
  fontSize: "30px",
  lineHeight: "38px",
}));

styled(TextField)(() => ({
  border: "1px solid  rgb(88, 89, 91,0.7)",
  borderRadius: "6px",
}));

styled(Grid)(() => ({
  margin: "auto",
  display: "flex",
  maxWidth: "411px",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
}));

const MainContainer = styled(Grid)(() => ({
  display: "flex",
  justifyContent: "space-between",
}));

const LoginText = styled(Typography)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginTop: "33px",
  color: "#4A2CF5",
  cursor: "pointer",
}));

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [warningMessage, setWarningMessage] = useState(loadWarning);
  const [outcomeSeverity, setOutcomeSeverity] = useState("success");
  const requestInFlight = useRef(false);
  const {remainingSeconds, feedback, setFeedback, beginCooldown} = useResendCooldown({
    storageKey: COOLDOWN_STORAGE_KEY,
  });

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestInFlight.current || remainingSeconds > 0) return;

    const normalizedEmail = email.toLowerCase().trim();
    const errors = {
      email: validateEmail(normalizedEmail),
    };
    setFormErrors(errors);
    if (errors?.email?.length) return;

    requestInFlight.current = true;
    setSubmitting(true);
    setFeedback("");
    setWarningMessage("");
    persistWarning("");
    let response;
    try {
      response = await forgotPasswordApi(normalizedEmail);
    } catch {
      response = undefined;
    } finally {
      requestInFlight.current = false;
      setSubmitting(false);
    }

    if (response?.status === 200) {
      setOutcomeSeverity("success");
      beginCooldown(SUCCESS_COOLDOWN_SECONDS, SUCCESS_MESSAGE);
      return;
    }

    const {detail} = response?.data || {};
    if (response?.status === 429) {
      const message = errorDetail(detail, RATE_LIMIT_MESSAGE);
      setWarningMessage(message);
      persistWarning(message);
      return;
    }

    setOutcomeSeverity("error");
    setFeedback(response === undefined
      ? CONNECTION_ERROR_MESSAGE
      : response?.status >= 500 ? SERVER_ERROR_MESSAGE : REQUEST_ERROR_MESSAGE);
  };

  return (
    <LightOnlyPage component={MainContainer} sx={{minHeight: "100vh"}}>
      <Grid
        style={{
          margin: "auto",
          display: "flex",
          maxWidth: "411px",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="forgot_div">
          <div className="wrap_login_first_part">
            <Box sx={authFormFieldsSx}>
              <Typography variant="mainText" mb={"11px"} component={"p"}>
                Forgot Password
              </Typography>
              <Typography variant="mainSubText" component={"p"} mb={"51px"}>
                Enter your email and we’ll send you a link to reset your password.
              </Typography>
              <Grid>
                <form onSubmit={handleSubmit}>
                  <Grid mb={"14px"}>
                    <LabelText label={"Email address"} isRequired={true} />
                  </Grid>
                  <TextFieldBox
                    variant="outlined"
                    type="email"
                    placeholder={"Email address"}
                    name={"email"}
                    inputProps={{"aria-label": "Email address"}}
                    value={email}
                    handleChange={handleEmailChange}
                    fullWidth
                    size="small"
                    error={formErrors}
                  />
                  <Grid mt={"33px"}>
                    <Button
                      data-testid="forgot-password-submit"
                      variant="contained"
                      type="submit"
                      disabled={submitting || remainingSeconds > 0}
                      fullWidth
                      disableElevation
                      sx={{ ...primaryButtonSx, mt: 0 }}
                    >
                      {submitting ? "Sending..." : "Send link"}
                    </Button>
                  </Grid>
                  {(feedback || warningMessage) && (
                    <Alert
                      data-testid="forgot-password-outcome"
                      severity={warningMessage ? "warning" : outcomeSeverity}
                      sx={{mt: 2}}
                    >
                      {warningMessage || feedback}
                    </Alert>
                  )}
                  <LoginText onClick={() => navigate("/")} variant="h6">
                    Back to Login
                  </LoginText>
                </form>
              </Grid>
            </Box>
          </div>
        </div>
      </Grid>
      <LoginLeftPart />
    </LightOnlyPage>
  );
};
